/**
 * Shared affordances fuer die Multi-File-Upload-Modals (Contracts + Supplier-Invoices):
 *  - DropZone (leerer State)
 *  - AddMoreFiles (Pluszeichen unter der Tabelle)
 *  - FileBadge (IMG/PDF/FILE-Indicator pro Row)
 */
import { cn } from '@/shared/lib/cn';
import { I } from '@/shared/icons';

export function DropZone({
  onFiles,
  hint,
}: {
  onFiles: (files: FileList | null) => void;
  hint: string;
}) {
  return (
    <label className="border-line/60 hover:border-sage/40 hover:bg-sage/5 text-ink-soft focus-within:border-sage block cursor-pointer rounded-md border border-dashed px-4 py-12 text-center transition-colors">
      <I name="Upload" size={20} className="text-ink-faint mx-auto mb-2" />
      <div className="text-sm">Dateien hierher ziehen oder tippen, um sie auszuwählen</div>
      <div className="text-ink-faint mt-1 text-xs">{hint}</div>
      <input type="file" multiple className="sr-only" onChange={(e) => onFiles(e.target.files)} />
    </label>
  );
}

export function AddMoreFiles({
  onFiles,
  disabled,
}: {
  onFiles: (files: FileList | null) => void;
  disabled: boolean;
}) {
  return (
    <label
      className={cn(
        'text-ink-faint hover:text-ink focus-within:text-ink mt-2 inline-flex min-h-11 cursor-pointer items-center gap-1.5 text-xs',
        disabled && 'pointer-events-none opacity-40',
      )}
    >
      <I name="Plus" size={11} />
      <span>Weitere Dateien hinzufügen</span>
      <input type="file" multiple className="sr-only" onChange={(e) => onFiles(e.target.files)} />
    </label>
  );
}

export function FileBadge({ file }: { file: File }) {
  const isImage = file.type.startsWith('image/');
  const isPdf = file.type === 'application/pdf';
  const label = isImage ? 'IMG' : isPdf ? 'PDF' : 'FILE';
  return (
    <span
      className={cn(
        'text-ink-faint mt-0.5 inline-flex h-5 w-7 shrink-0 items-center justify-center rounded border font-mono text-[9px] uppercase tabular-nums',
        isImage && 'text-sage border-sage/30',
        isPdf && 'border-ink-faint',
        !isImage && !isPdf && 'border-line',
      )}
    >
      {label}
    </span>
  );
}

export function RowStatusOrRemove({
  status,
  onRemove,
  disabled,
}: {
  status: 'idle' | 'uploading' | 'done' | 'error';
  onRemove: () => void;
  disabled: boolean;
}) {
  if (status === 'idle' || status === 'error') {
    return (
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="text-ink-faint hover:text-sealwax flex h-11 w-11 items-center justify-center rounded disabled:opacity-40"
        aria-label="Zeile entfernen"
      >
        <I name="Close" size={14} />
      </button>
    );
  }
  // Verbleibend: 'uploading' | 'done' ('idle'/'error' sind oben abgehandelt).
  // Statusfarbe nie ohne Text (§11) — das Symbol allein sagte nicht, was passiert ist.
  return status === 'done' ? (
    <span className="text-sage flex h-11 w-11 items-center justify-center" title="Hochgeladen">
      <I name="Check" size={16} />
      <span className="sr-only">Hochgeladen</span>
    </span>
  ) : (
    <span className="text-ink-faint flex h-11 w-11 items-center justify-center" title="Lädt hoch…">
      <I name="Refresh" size={16} className="animate-spin" />
      <span className="sr-only">Lädt hoch…</span>
    </span>
  );
}
